'use client'

import { IntegrationsModal } from '@/components/Integration/IntegrationsModal'
import PersonalizationPanel from '@/components/PersonalizationPanel/PersonalizationPanel'
import { analytics } from '@/lib/analytics'
import {
  useSessionAwareConnectedIntegrationsQuery,
  useSessionAwareOnboardingQuery,
  useUpdateOnboardingDataMutation,
} from '@/lib/features/user/userApi'
import { AnimatePresence, motion } from 'framer-motion'
import { CheckCircle2, ChevronDown, Loader2, Sparkles, X } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

interface Template {
  id: string
  name: string
  description: string
  features: string[]
  recommended?: boolean
  href: string
}

// Core templates - moved outside component to prevent recreation on every render
const TEMPLATES: Template[] = [
  {
    id: 'seo-tech-audit',
    name: 'Technical SEO Audit',
    description: 'Find and fix technical issues affecting your search rankings',
    features: [
      'Site crawl analysis',
      'Core Web Vitals',
      'Broken links detection',
      'Schema markup check',
    ],
    recommended: true,
    href: '/template/3920366b-5a2b-45b1-99f3-376a1049e7a2',
  },
  {
    id: 'google-ads-campaign',
    name: 'Google Ads Campaign Builder',
    description: 'Create and launch high-converting Google Ads campaigns',
    features: [
      'Keyword research',
      'Ad copy generation',
      'Budget optimization',
      'Performance tracking',
    ],
    href: '/template/c5b3c5b5-ed7d-4d2c-9282-44a354a67a80',
  },
  {
    id: 'competitor-ad-spy',
    name: 'Competitor Ad Intelligence',
    description: "Discover and analyze your competitors' advertising strategies",
    features: [
      'Ad copy extraction',
      'Landing page analysis',
      'Keyword targeting',
      'Budget estimates',
    ],
    href: '/template/b2f6081b-de6b-5394-aced-f0b4736c3ef6',
  },
]

// Business Info Card Component
function BusinessInfoCard({ onReviewClick }: { onReviewClick: () => void }) {
  return (
    <div className="flex flex-1 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="flex h-48 items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
        {/* Placeholder illustration */}
        <div className="text-center">
          <div className="mx-auto mb-2 h-16 w-16 rounded-full bg-blue-100" />
        </div>
      </div>
      <div className="px-6 pb-6 pt-3">
        <h3 className="font-space-grotesk mb-1 text-base font-medium">Review your business info</h3>
        <p className="font-space-grotesk mb-6 flex-grow text-sm text-gray-600">
          Check the information we&apos;ve gathered from your website and make sure it&apos;s
          accurate
        </p>
        <button
          onClick={onReviewClick}
          className="font-space-grotesk w-full rounded-[8px] border border-gray-300 bg-white px-4 py-2.5 text-sm font-light text-gray-900 transition-all hover:bg-gray-50 active:scale-[0.98]"
        >
          Review info
        </button>
      </div>
    </div>
  )
}

// Analysis Card Component
function AnalysisCard({
  router,
  updateOnboarding,
  onboardingData,
}: {
  router: ReturnType<typeof useRouter>
  updateOnboarding: ReturnType<typeof useUpdateOnboardingDataMutation>[0]
  onboardingData: ReturnType<typeof useSessionAwareOnboardingQuery>['data']
}) {
  const [isNavigating, setIsNavigating] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const runAnalysis = () => {
    // Track analysis card click
    analytics.onboarding.postOnboardingAction(
      'get_started_analysis_clicked',
      '/copilot/f445df2f-f088-40b1-8579-04133b1add7a'
    )

    setIsNavigating(true)

    // Persist completion state (fire and forget)
    updateOnboarding({
      getStartedTasks: {
        ...onboardingData?.getStartedTasks,
        analysis: true,
      },
    }).catch((error) => {
      console.error('Failed to update onboarding data:', error)
    })

    // Navigate after delay
    timeoutRef.current = setTimeout(() => {
      router.push('/copilot/f445df2f-f088-40b1-8579-04133b1add7a')
    }, 400)
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-col">
        <div className="flex h-48 items-center justify-center bg-gradient-to-br from-orange-50 to-pink-50">
          {/* Placeholder illustration */}
          <div className="flex gap-2">
            <div className="h-12 w-12 rounded-lg bg-orange-200" />
            <div className="h-12 w-12 rounded-lg bg-pink-200" />
          </div>
        </div>
        <div className="px-6 pb-6 pt-3">
          <h3 className="font-space-grotesk mb-1 text-base font-medium">Run marketing analysis</h3>
          <p className="font-space-grotesk mb-6 flex-grow text-sm text-gray-600">
            Get insights on SEO, competitors, and gaps in your marketing strategy
          </p>
          <button
            onClick={runAnalysis}
            disabled={isNavigating}
            className="font-space-grotesk relative w-full rounded-[8px] bg-black px-4 py-2.5 text-sm font-light text-white transition-all hover:bg-zinc-800 active:scale-[0.98] disabled:opacity-75"
          >
            {isNavigating ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading...
              </span>
            ) : (
              'Run analysis'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function GetStartedCards() {
  const router = useRouter()
  const { data: onboardingData } = useSessionAwareOnboardingQuery()
  const { data: connectedIntegrations, refetch: refetchIntegrations } =
    useSessionAwareConnectedIntegrationsQuery()
  const [updateOnboardingData] = useUpdateOnboardingDataMutation()
  const hasInitialized = useRef(false)
  const hasAutoCompletedIntegration = useRef(false)
  const templateTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const [tasks, setTasks] = useState({
    analysis: false,
    template: false,
    integration: false,
  })

  // Check if any integrations are connected (memoized to prevent recalculation)
  const hasConnectedIntegrations = useMemo(
    () => connectedIntegrations?.providerIds && connectedIntegrations.providerIds.length > 0,
    [connectedIntegrations?.providerIds]
  )

  // Load persisted task completion state from onboarding data (only once on mount)
  useEffect(() => {
    if (onboardingData?.getStartedTasks && !hasInitialized.current) {
      setTasks((prev) => ({
        ...prev,
        analysis: onboardingData.getStartedTasks?.analysis || false,
        template: onboardingData.getStartedTasks?.template || false,
        integration:
          onboardingData.getStartedTasks?.integration || hasConnectedIntegrations || false,
      }))
      hasInitialized.current = true
    }
  }, [onboardingData, hasConnectedIntegrations])

  // Auto-complete integration task when integrations are connected
  useEffect(() => {
    if (
      hasConnectedIntegrations &&
      !tasks.integration &&
      hasInitialized.current &&
      !hasAutoCompletedIntegration.current
    ) {
      hasAutoCompletedIntegration.current = true
      setTasks((prev) => ({ ...prev, integration: true }))

      // Persist to backend
      updateOnboardingData({
        getStartedTasks: {
          ...onboardingData?.getStartedTasks,
          integration: true,
        },
      }).catch((error) => {
        console.error('Failed to update onboarding data:', error)
      })
    }
  }, [hasConnectedIntegrations, tasks.integration, onboardingData, updateOnboardingData])

  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)
  const [isNavigatingToTemplate, setIsNavigatingToTemplate] = useState(false)
  const [isIntegrationsModalOpen, setIsIntegrationsModalOpen] = useState(false)
  const [isPersonalizationPanelOpen, setIsPersonalizationPanelOpen] = useState(false)

  // Start with true to prevent flash, then update based on actual data
  const [isDismissed, setIsDismissed] = useState(true)
  const [hasLoadedDismissedState, setHasLoadedDismissedState] = useState(false)

  // Determine which step should be expanded by default
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set())

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (templateTimeoutRef.current) {
        clearTimeout(templateTimeoutRef.current)
      }
    }
  }, [])

  // Load dismissed state from onboarding data
  useEffect(() => {
    if (onboardingData !== undefined && !hasLoadedDismissedState) {
      setIsDismissed(onboardingData?.getStartedCardDismissed || false)
      setHasLoadedDismissedState(true)
    }
  }, [onboardingData, hasLoadedDismissedState])

  // Memoize derived values to prevent recalculation on every render
  const { completedTasks, totalTasks, allTasksComplete, progressPercentage } = useMemo(() => {
    const completed = Object.values(tasks).filter(Boolean).length
    const total = Object.keys(tasks).length
    const allComplete = completed === total
    const percentage = (completed / total) * 100
    return {
      completedTasks: completed,
      totalTasks: total,
      allTasksComplete: allComplete,
      progressPercentage: percentage,
    }
  }, [tasks])

  // Auto-expand the earliest not completed step (only if not all complete)
  useEffect(() => {
    if (allTasksComplete) {
      setExpandedSteps(new Set())
    } else if (!tasks.analysis) {
      setExpandedSteps(new Set([1]))
    } else if (!tasks.template) {
      setExpandedSteps(new Set([2]))
    } else if (!tasks.integration) {
      setExpandedSteps(new Set([3]))
    }
  }, [tasks.analysis, tasks.template, tasks.integration, allTasksComplete])

  const toggleStep = useCallback((stepNumber: number) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev)
      if (next.has(stepNumber)) {
        next.delete(stepNumber)
      } else {
        next.add(stepNumber)
      }
      return next
    })
  }, [])

  const handleTemplateSelect = useCallback(
    (template: Template) => {
      // Track template selection
      analytics.onboarding.postOnboardingAction('get_started_template_selected', template.href)

      setSelectedTemplate(template.id)
      setIsNavigatingToTemplate(true)

      // Persist completion state (fire and forget)
      updateOnboardingData({
        getStartedTasks: {
          ...onboardingData?.getStartedTasks,
          template: true,
        },
      }).catch((error) => {
        console.error('Failed to update onboarding data:', error)
      })

      // Navigate to template after delay (with cleanup)
      templateTimeoutRef.current = setTimeout(() => {
        router.push(template.href)
      }, 400)
    },
    [onboardingData?.getStartedTasks, router, updateOnboardingData]
  )

  const handleDismiss = useCallback(() => {
    setIsDismissed(true)

    updateOnboardingData({
      getStartedCardDismissed: true,
    }).catch((error) => {
      console.error('Failed to update onboarding data:', error)
    })
  }, [updateOnboardingData])

  const handleOpenPersonalization = useCallback(() => {
    analytics.onboarding.postOnboardingAction('get_started_personalization_opened')
    setIsPersonalizationPanelOpen(true)
  }, [])

  const handleCloseIntegrations = useCallback(() => {
    setIsIntegrationsModalOpen(false)
    refetchIntegrations()
  }, [refetchIntegrations])

  const handleIntegrationsClick = useCallback(() => {
    analytics.onboarding.postOnboardingAction('get_started_integrations_clicked')
    setIsIntegrationsModalOpen(true)
  }, [])

  const handleClosePersonalization = useCallback(() => {
    setIsPersonalizationPanelOpen(false)
  }, [])

  return (
    <>
      <AnimatePresence>
        {!isDismissed && (
          <motion.div
            initial={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="mb-8 overflow-hidden"
          >
            <div className="mx-auto max-w-[880px] rounded-2xl bg-white p-8 shadow-lg">
              {/* Header */}
              <div className="mb-4 flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="font-space-grotesk text-xl font-medium text-gray-900">
                    Get Started with Sprites
                  </h2>
                  <p className="font-space-grotesk mt-1 text-sm text-gray-600">
                    Take these steps to unlock the full power of automation
                  </p>
                </div>
                <button
                  onClick={handleDismiss}
                  className="ml-4 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                  aria-label="Dismiss"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Progress Indicator - Subtle and compact */}
              <div className="mb-6 flex items-center gap-3">
                <span className="font-space-grotesk text-sm text-gray-600">
                  {completedTasks} / {totalTasks} completed
                </span>
                <div className="h-1.5 w-32 overflow-hidden rounded-full bg-gray-200">
                  <div
                    className="h-full bg-gray-900 transition-all duration-300"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              </div>

              {/* Step 1: Marketing Analysis */}
              <div className="mb-4 overflow-hidden rounded-xl border border-gray-200">
                <button
                  onClick={() => toggleStep(1)}
                  className="flex w-full items-center justify-between bg-white p-4 text-left transition-colors hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
                        tasks.analysis ? 'bg-green-500' : 'border-2 border-gray-300 bg-white'
                      }`}
                    >
                      {tasks.analysis ? (
                        <CheckCircle2 className="h-5 w-5 text-white" />
                      ) : (
                        <div className="h-3 w-3 rounded-full border-2 border-gray-300" />
                      )}
                    </div>
                    <h2 className="font-space-grotesk text-base font-medium text-gray-900">
                      Find gaps in your marketing strategy
                    </h2>
                  </div>
                  <ChevronDown
                    className={`h-5 w-5 text-gray-400 transition-transform ${
                      expandedSteps.has(1) ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {expandedSteps.has(1) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="border-t border-gray-200 bg-gray-50 p-4"
                  >
                    <div className="flex flex-col gap-4 md:flex-row">
                      <AnalysisCard
                        router={router}
                        updateOnboarding={updateOnboardingData}
                        onboardingData={onboardingData}
                      />
                      <BusinessInfoCard onReviewClick={handleOpenPersonalization} />
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Step 2: Choose Template */}
              <div className="mb-4 overflow-hidden rounded-xl border border-gray-200">
                <button
                  onClick={() => toggleStep(2)}
                  className="flex w-full items-center justify-between bg-white p-4 text-left transition-colors hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
                        tasks.template ? 'bg-green-500' : 'border-2 border-gray-300 bg-white'
                      }`}
                    >
                      {tasks.template ? (
                        <CheckCircle2 className="h-5 w-5 text-white" />
                      ) : (
                        <div className="h-3 w-3 rounded-full border-2 border-gray-300" />
                      )}
                    </div>
                    <h2 className="font-space-grotesk text-base font-medium text-gray-900">
                      Choose your template
                    </h2>
                  </div>
                  <ChevronDown
                    className={`h-5 w-5 text-gray-400 transition-transform ${
                      expandedSteps.has(2) ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {expandedSteps.has(2) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="border-t border-gray-200 bg-gray-50 p-4"
                  >
                    <div>
                      <div className="mb-4 grid gap-4 md:grid-cols-3">
                        {TEMPLATES.map((template) => (
                          <div
                            key={template.id}
                            onClick={() => handleTemplateSelect(template)}
                            className={`flex cursor-pointer flex-col rounded-xl border p-4 shadow-sm transition-all hover:shadow-md ${
                              selectedTemplate === template.id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 bg-white'
                            }`}
                          >
                            <div className="mb-3 flex items-start justify-between">
                              <div className="flex-1">
                                <div className="mb-1 flex items-center gap-2">
                                  <h3 className="font-space-grotesk text-sm font-medium">
                                    {template.name}
                                  </h3>
                                  {template.recommended && (
                                    <span className="flex items-center gap-1 rounded bg-purple-100 px-2 py-1 text-xs text-purple-700">
                                      <Sparkles className="h-3 w-3" />
                                      Recommended
                                    </span>
                                  )}
                                </div>
                                <p className="font-space-grotesk text-xs text-gray-600">
                                  {template.description}
                                </p>
                              </div>
                              {selectedTemplate === template.id && (
                                <CheckCircle2 className="ml-2 h-6 w-6 flex-shrink-0 text-blue-600" />
                              )}
                            </div>

                            <div className="mb-4 flex-1 space-y-2">
                              {template.features.map((feature, index) => (
                                <div
                                  key={index}
                                  className="flex items-center gap-2 text-sm text-gray-700"
                                >
                                  <div className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                                  <span className="font-space-grotesk text-xs">{feature}</span>
                                </div>
                              ))}
                            </div>

                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleTemplateSelect(template)
                              }}
                              disabled={isNavigatingToTemplate}
                              className={`font-space-grotesk mt-auto w-full rounded-[8px] px-4 py-2 text-sm font-light transition-all active:scale-[0.98] disabled:opacity-75 ${
                                selectedTemplate === template.id
                                  ? 'bg-black text-white hover:bg-zinc-800'
                                  : 'border border-gray-300 bg-white text-gray-900 hover:bg-gray-50'
                              }`}
                            >
                              {isNavigatingToTemplate && selectedTemplate === template.id ? (
                                <span className="flex items-center justify-center gap-2">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Loading...
                                </span>
                              ) : selectedTemplate === template.id ? (
                                'Selected'
                              ) : (
                                'Select Template'
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Step 3: Connect Integrations */}
              <div className="overflow-hidden rounded-xl border border-gray-200">
                <button
                  onClick={() => toggleStep(3)}
                  className="flex w-full items-center justify-between bg-white p-4 text-left transition-colors hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
                        tasks.integration ? 'bg-green-500' : 'border-2 border-gray-300 bg-white'
                      }`}
                    >
                      {tasks.integration ? (
                        <CheckCircle2 className="h-5 w-5 text-white" />
                      ) : (
                        <div className="h-3 w-3 rounded-full border-2 border-gray-300" />
                      )}
                    </div>
                    <h2 className="font-space-grotesk text-base font-medium text-gray-900">
                      Connect your integrations
                    </h2>
                  </div>
                  <ChevronDown
                    className={`h-5 w-5 text-gray-400 transition-transform ${
                      expandedSteps.has(3) ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {expandedSteps.has(3) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="border-t border-gray-200 bg-gray-50 p-4"
                  >
                    <div className="rounded-xl border border-gray-200 bg-white p-6">
                      <div className="mb-6">
                        <p className="font-space-grotesk text-sm text-gray-600">
                          Connect popular tools and services to enhance your workflow and automate
                          processes.
                        </p>
                      </div>

                      <div className="grid gap-4 md:grid-cols-3">
                        <div className="flex flex-col rounded-lg bg-gray-50 p-4">
                          <h3 className="font-space-grotesk mb-1 text-sm font-medium">
                            Google Drive
                          </h3>
                          <p className="font-space-grotesk mb-4 text-xs text-gray-600">
                            Access and manage your files
                          </p>
                          <button
                            onClick={handleIntegrationsClick}
                            className="font-space-grotesk mt-auto rounded-[8px] bg-black px-4 py-2 text-sm font-light text-white transition-all hover:bg-zinc-800"
                          >
                            Connect
                          </button>
                        </div>

                        <div className="flex flex-col rounded-lg bg-gray-50 p-4">
                          <h3 className="font-space-grotesk mb-1 text-sm font-medium">WordPress</h3>
                          <p className="font-space-grotesk mb-4 text-xs text-gray-600">
                            Sync content and automate posts
                          </p>
                          <button
                            onClick={handleIntegrationsClick}
                            className="font-space-grotesk mt-auto rounded-[8px] bg-black px-4 py-2 text-sm font-light text-white transition-all hover:bg-zinc-800"
                          >
                            Connect
                          </button>
                        </div>

                        <div className="flex flex-col rounded-lg bg-gray-50 p-4">
                          <h3 className="font-space-grotesk mb-1 text-sm font-medium">
                            Google Ads
                          </h3>
                          <p className="font-space-grotesk mb-4 text-xs text-gray-600">
                            Optimize ad campaigns
                          </p>
                          <button
                            onClick={handleIntegrationsClick}
                            className="font-space-grotesk mt-auto rounded-[8px] bg-black px-4 py-2 text-sm font-light text-white transition-all hover:bg-zinc-800"
                          >
                            Connect
                          </button>
                        </div>
                      </div>

                      <div className="mt-6 text-center">
                        <button
                          onClick={handleIntegrationsClick}
                          className="font-space-grotesk rounded-[8px] border border-gray-300 bg-white px-4 py-2 text-sm font-light text-gray-900 transition-all hover:bg-gray-50 active:scale-[0.98]"
                        >
                          All integrations
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <IntegrationsModal isOpen={isIntegrationsModalOpen} onClose={handleCloseIntegrations} />

      <PersonalizationPanel
        isOpen={isPersonalizationPanelOpen}
        onClose={handleClosePersonalization}
      />
    </>
  )
}
